/**
 * The consolidated DBS validation rule set (DBS-344): shared types.
 *
 * Everything in this directory is copied verbatim into the three portals and the e2e suite by
 * api/scripts/sync_dbs_rules.sh, so it must stay free of repo-specific imports and of language features the
 * portals' ES5 target cannot downlevel (no iteration over Sets or strings, no `Object.entries`).
 */

export type Nullable<T> = T | null | undefined

/** The applicant portal pages; a rule that runs on a page is listed under it */
export type Page = 'identity' | 'names' | 'birth' | 'addresses' | 'documents'

/**
 * Where an evaluation is happening:
 * - a page: the applicant is saving that page, so only that page's rules run (an error on a field the page does not
 *   show would block the save with no way to see why);
 * - confirm: the applicant's confirm & complete screen, which runs every page rule plus the cross-page ones;
 * - server: every rule, including the ones only the server can judge (completeness, Registered Body details).
 */
export type At = Page | 'confirm' | 'server'

/** The earliest point a rule runs: on its pages, on the confirm screen, or only on the server */
export type Stage = 'page' | 'confirm' | 'server'

export type Severity = 'error' | 'warning'

export type IssuePath = (string | number)[]

export type Issue = {
  ruleId: string,
  path: IssuePath,
  message: string,
  severity: Severity,
  /** The DBS application-level error codes this rule prevents (e-Bulk spec 3.6.x "Application-Level Error Codes") */
  dbsCodes: readonly number[],
}

/** What a rule's check returns; the evaluator adds the rule's id, severity and codes */
export type RawIssue = { path: IssuePath, message: string }

/** Looks up a country list label for a country code, e.g. "IN" -> "India". Each repo supplies its own list. */
export type CountryLabelFor = (countryCode: string) => string | undefined

/** The Registered Body side of an application, known only at countersigning and the batch */
export type RbContext = {
  rbNumber?: Nullable<string>,
  csNumber?: Nullable<string>,
  /** The customer's verification confirmed the current address details */
  addressDetailsChecked?: Nullable<boolean>,
}

export type RuleContext = {
  at: At,
  /** ISO date (YYYY-MM-DD), injectable so date rules are testable */
  today: string,
  /** Treat the data as a finished application: required fields and the identity/RB checks apply */
  complete: boolean,
  countryLabelFor?: CountryLabelFor,
  rb?: RbContext,
}

export type Rule<T> = {
  /** Stable dotted id, e.g. `address.postcode.format`; messages, fixtures and tests key on it */
  id: string,
  title: string,
  dbsCodes: readonly number[],
  /** References into the e-Bulk Business Message Specification: `4#20` is section-4 row 20, `3.7.4` a section */
  spec: readonly string[],
  severity: Severity,
  /** The applicant pages that run this rule (empty for cross-page and server-only rules) */
  pages: readonly Page[],
  stage: Stage,
  check: (data: T, ctx: RuleContext) => RawIssue[],
}

export type EvaluateOptions = {
  at: At,
  today?: string,
  complete?: boolean,
  countryLabelFor?: CountryLabelFor,
  rb?: RbContext,
}

// --- The application data, as a structural type -------------------------------------------------------------------
// Every field is optional and nullable so the api's stored shape, the portals' form shapes (which use null and ''
// for "not entered") and partial test fixtures all fit without casts. Rules treat null, undefined and '' as absent.

export type AddressData = {
  AddressLine1?: Nullable<string>,
  AddressLine2?: Nullable<string>,
  AddressTown?: Nullable<string>,
  AddressCounty?: Nullable<string>,
  Postcode?: Nullable<string>,
  CountryCode?: Nullable<string>,
}

export type OtherNameData = {
  Name?: Nullable<string>,
  UsedFrom?: Nullable<string>,
  UsedTo?: Nullable<string>,
}

export type ApplicationData = {
  Title?: Nullable<string>,
  Forename?: Nullable<string>,
  Middlenames?: Nullable<{ Middlename?: Nullable<Nullable<string>[]> }>,
  PresentSurname?: Nullable<string>,
  CurrentAddress?: Nullable<{
    Address?: Nullable<AddressData>,
    ResidentFromGyearMonth?: Nullable<string>,
  }>,
  PreviousAddress?: Nullable<Nullable<{
    Address?: Nullable<AddressData>,
    ResidentDates?: Nullable<{
      ResidentFromGyearMonth?: Nullable<string>,
      ResidentToGyearMonth?: Nullable<string>,
    }>,
  }>[]>,
  DateOfBirth?: Nullable<string>,
  Gender?: Nullable<string>,
  NiNumber?: Nullable<string>,
  AdditionalApplicantDetails?: Nullable<{
    BirthSurname?: Nullable<string>,
    BirthSurnameUntil?: Nullable<string>,
    OtherSurnames?: Nullable<{ OtherSurname?: Nullable<Nullable<OtherNameData>[]> }>,
    OtherForenames?: Nullable<{ OtherForename?: Nullable<Nullable<OtherNameData>[]> }>,
    BirthTown?: Nullable<string>,
    BirthCounty?: Nullable<string>,
    BirthCountry?: Nullable<string>,
    BirthNationality?: Nullable<string>,
    ContactNumber?: Nullable<string>,
    UnspentConvictions?: Nullable<string>,
    DeclarationByApplicant?: Nullable<string>,
    LanguagePreference?: Nullable<string>,
  }>,
  ApplicantIdentityDetails?: Nullable<{
    IdentityVerified?: Nullable<string>,
    EvidenceCheckedBy?: Nullable<string>,
    PassportDetails?: Nullable<{
      PassportNumber?: Nullable<string>,
      PassportDob?: Nullable<string>,
      PassportNationality?: Nullable<string>,
      PassportIssueDate?: Nullable<string>,
    }>,
    DriverLicenceDetails?: Nullable<{
      DriverLicenceNumber?: Nullable<string>,
      DriverLicenceDOB?: Nullable<string>,
      DriverLicenceType?: Nullable<string>,
      DriverLicenceValidFrom?: Nullable<string>,
      DriverLicenceIssueCountry?: Nullable<string>,
    }>,
  }>,
}

/** A job role as the portals and the api hold it (the DBS receives it as PositionAppliedFor + RBdetails flags) */
export type JobData = {
  role?: Nullable<string>,
  application_type?: Nullable<string>,
  application_workforce?: Nullable<string>,
  working_with_children?: Nullable<boolean>,
  working_with_adults?: Nullable<boolean>,
  working_at_home_address?: Nullable<boolean>,
  volunteer?: Nullable<boolean>,
}

/** True when a value counts as entered: not null, not undefined and not an empty string */
export const present = (value: unknown): value is string | number | boolean | object =>
  value !== null && value !== undefined && value !== ''

/** True when `page`'s rules should run for this evaluation */
export const owned = (ctx: RuleContext, page: Page): boolean =>
  ctx.at === 'confirm' || ctx.at === 'server' || ctx.at === page
